import { Link } from 'react-router-dom';

export default function ProcessoPage() {
  const steps = [
    {
      number: '01',
      title: 'Primeiro Contacto',
      description: 'Preenche o formulário de contacto com algumas perguntas reflexivas. Não são perguntas demográficas — são perguntas que ajudam a perceber o momento atual e a necessidade.',
    },
    {
      number: '02',
      title: 'Conversa Inicial',
      description: 'Uma conversa sem compromisso, tipicamente de 30 minutos, para perceber se faz sentido trabalharmos juntos e, se sim, que formato seria mais adequado.',
    },
    {
      number: '03',
      title: 'Proposta de Acompanhamento',
      description: 'Com base na conversa, sugiro um nível de acompanhamento. A proposta inclui frequência, formato e expectativas mútuas. A decisão é sempre sua.',
    },
    {
      number: '04',
      title: 'Início do Trabalho',
      description: 'Se houver alinhamento, começamos. As primeiras sessões servem para calibrar a abordagem e garantir que o formato responde às necessidades.',
    },
  ];

  const principles = [
    {
      title: 'Seletividade',
      description: 'Este acompanhamento não é para toda a gente. Procuro trabalhar com pessoas que reconhecem a necessidade de clareza e estão dispostas a fazer o trabalho necessário.',
    },
    {
      title: 'Transparência',
      description: 'Não há vendas ocultas nem upselling. O nível adequado decide-se em conversa, com base nas necessidades reais, não em estratégias comerciais.',
    },
    {
      title: 'Flexibilidade',
      description: 'A transição entre níveis é possível mas não automática. Se as necessidades mudarem, reavaliamos. Mas não há pressão para "subir" de nível.',
    },
  ];

  return (
    <main className="pt-24" data-testid="processo-page">
      {/* Hero */}
      <section className="section">
        <div className="page-container">
          <div className="max-w-3xl">
            <span className="label-mono mb-4 block">Processo</span>
            <h1 className="heading-display mb-8">
              Como Começar
            </h1>
            <p className="body-text text-lg">
              O processo é simples e transparente. 
              Não há surpresas, não há pressão, não há vendas agressivas.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section section-muted" data-testid="timeline-section">
        <div className="page-container">
          <div className="max-w-3xl">
            <div className="relative pl-8 md:pl-12">
              {/* Vertical Line */}
              <div className="timeline-line" />
              
              {steps.map((step, index) => (
                <div 
                  key={index} 
                  className="relative pb-12 last:pb-0"
                  data-testid={`step-${step.number}`}
                >
                  {/* Dot */}
                  <div className="timeline-dot" />
                  
                  <span className="label-mono mb-3 block">{step.number}</span>
                  <h3 className="font-serif text-2xl text-[#1A1A1A] mb-3">
                    {step.title}
                  </h3>
                  <p className="body-text">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section" data-testid="principles-section">
        <div className="page-container">
          <div className="mb-12">
            <span className="label-mono mb-4 block">Princípios</span>
            <h2 className="heading-section">
              Como trabalho com quem trabalho
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {principles.map((principle, index) => (
              <div key={index} className="border-t border-[#E6E4DC] pt-6">
                <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">
                  {principle.title}
                </h3>
                <p className="body-text">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ-like section */}
      <section className="section section-muted" data-testid="faq-section">
        <div className="page-container">
          <div className="max-w-3xl">
            <span className="label-mono mb-4 block">Expectativas</span>
            <h2 className="heading-section mb-12">
              Antes de iniciar contacto
            </h2>

            <div className="space-y-8">
              <div className="border-b border-[#E6E4DC] pb-8">
                <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">
                  Não há compra direta
                </h3>
                <p className="body-text">
                  Não existe forma de "comprar" um acompanhamento sem conversa prévia. 
                  Isto é intencional: o trabalho só faz sentido quando há alinhamento 
                  real entre necessidade e oferta.
                </p>
              </div>

              <div className="border-b border-[#E6E4DC] pb-8">
                <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">
                  A conversa inicial não tem custo
                </h3>
                <p className="body-text">
                  A primeira conversa serve para perceber se há fit. 
                  Não há compromisso, não há pressão. 
                  Se não fizer sentido, seguimos caminhos diferentes.
                </p>
              </div>

              <div className="pb-8">
                <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">
                  A decisão é mútua
                </h3>
                <p className="body-text">
                  Assim como você decide se quer trabalhar comigo, 
                  eu decido se consigo ajudar genuinamente. 
                  Não aceito trabalhar com quem não acredito poder ajudar.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="page-container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-section mb-6">
              Pronto para uma conversa?
            </h2>
            <p className="body-text mb-8">
              O formulário de contacto inclui algumas perguntas reflexivas. 
              Reserve alguns minutos para responder com atenção.
            </p>
            <Link to="/contacto" className="btn-primary" data-testid="cta-contact">
              Iniciar Conversa
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
